import { Pipe, PipeTransform } from '@angular/core';
import { EStatus } from '../enums/status.enum';

export interface StatusBadgeConfig {
  label: string;
  cssClass: string;
  icon: string;
}

@Pipe({
  name: 'statusBadge',
})
export class StatusBadgePipe implements PipeTransform {
  private readonly statusMap: Record<EStatus, StatusBadgeConfig> = {
    // Application statuses
    [EStatus.PENDING]: {
      label: 'Pending',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.APPROVED]: {
      label: 'Approved',
      cssClass: 'bg-green-100 text-green-800 border border-green-300',
      icon: 'check_circle',
    },
    [EStatus.REJECTED]: {
      label: 'Rejected',
      cssClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: 'cancel',
    },
    [EStatus.DRAFT]: {
      label: 'Draft',
      cssClass: 'bg-[#FFD54F] text-[#000] border border-[#FF7000]',
      icon: 'edit_note',
    },
    [EStatus.SUBMITTED]: {
      label: 'Submitted',
      cssClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: 'send',
    },
    [EStatus.RESUBMITTED]: {
      label: 'Resubmitted',
      cssClass: 'bg-indigo-100 text-indigo-800 border border-indigo-300',
      icon: 'replay',
    },
    [EStatus.UNDER_REVIEW]: {
      label: 'Under Review',
      cssClass: 'bg-purple-100 text-purple-800 border border-purple-300',
      icon: 'rate_review',
    },
    [EStatus.PROCESSING]: {
      label: 'Processing',
      cssClass: 'bg-orange-100 text-orange-800 border border-orange-300',
      icon: 'autorenew',
    },
    [EStatus.COMPLETED]: {
      label: 'Completed',
      cssClass: 'bg-teal-100 text-teal-800 border border-teal-300',
      icon: 'task_alt',
    },

    // User statuses
    [EStatus.ACTIVE]: {
      label: 'Active',
      cssClass: 'bg-green-50 text-green-700 border border-green-200',
      icon: 'check_circle',
    },
    [EStatus.INACTIVE]: {
      label: 'Inactive',
      cssClass: 'bg-gray-50 text-gray-700 border border-gray-200',
      icon: 'remove_circle',
    },
    [EStatus.SUSPENDED]: {
      label: 'Suspended',
      cssClass: 'bg-red-50 text-red-700 border border-red-200',
      icon: 'block',
    },

    // Payment statuses
    [EStatus.PAID]: {
      label: 'Paid',
      cssClass: 'bg-green-100 text-green-800 border border-green-300',
      icon: 'paid',
    },
    [EStatus.UNPAID]: {
      label: 'Unpaid',
      cssClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: 'money_off',
    },
    [EStatus.OVERDUE]: {
      label: 'Overdue',
      cssClass: 'bg-orange-100 text-orange-800 border border-orange-300',
      icon: 'error',
    },
    [EStatus.REFUNDED]: {
      label: 'Refunded',
      cssClass: 'bg-teal-100 text-teal-800 border border-teal-300',
      icon: 'currency_exchange',
    },
    [EStatus.QUERIED]: {
      label: 'Queried',
      cssClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: 'warning_amber',
    },
    [EStatus.PENDING_REGISTRY_APPROVAL]: {
      label: 'Pending Registry Approval',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.PENDING_KYC_APPROVAL]: {
      label: 'Pending KYC Approval',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.KYC_REJECTED]: {
      label: 'KYC Rejected',
      cssClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: 'cancel',
    },
    [EStatus.PENDING_FIRST_REGISTRY_APPROVAL]: {
      label: 'Pending First Registry Approval',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.FIRST_REGISTRY_QUERIED]: {
      label: 'First Registry Queried',
      cssClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: 'warning_amber',
    },
    [EStatus.PENDING_SECOND_REGISTRY_APPROVAL]: {
      label: 'Pending Second Registry Approval',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.SECOND_REGISTRY_QUERIED]: {
      label: 'Second Registry Queried',
      cssClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: 'warning_amber',
    },
    [EStatus.PENDING_REGISTRAR_APPROVAL]: {
      label: 'Pending Registrar Approval',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.REGISTRAR_QUERIED]: {
      label: 'Registrar Queried',
      cssClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: 'warning_amber',
    },
    [EStatus.FINALLY_APPROVED]: {
      label: 'Finally Approved',
      cssClass: 'bg-green-100 text-green-800 border border-green-300',
      icon: 'check_circle',
    },
    [EStatus.FINALLY_REJECTED]: {
      label: 'Finally Rejected',
      cssClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: 'cancel',
    },
    [EStatus.REGISTRY_RECOMMENDED]: {
      label: 'Registry Recommended',
      cssClass: 'bg-blue-100 text-blue-800 border border-blue-300',
      icon: 'warning_amber',
    },
    [EStatus.AWAITING_OFFICE_CONFIRMATION]: {
      label: 'Awaiting Office Confirmation',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },
    [EStatus.FAILED]: {
      label: 'Failed',
      cssClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: 'cancel',
    },
    [EStatus.PENDING_VERIFICATION]: {
      label: 'Pending Verification',
      cssClass: 'bg-[#FFE0B2] text-[#FF7000] border border-[#FF7000]',
      icon: 'schedule',
    },
    [EStatus.PARTIALLY_APPROVED]: {
      label: 'Partially Approved',
      cssClass: 'bg-[#FFE0B2] text-[#FF7000] border border-[#FF7000]',
      icon: 'schedule',
    },
    [EStatus.PAYMENT_PENDING]: {
      label: 'Payment Pending',
      cssClass: 'bg-[#FFD54F] text-[#000] border border-[#FF7000]',
      icon: 'schedule',
    },
    [EStatus.DORMANT]: {
      label: 'Dormant',
      cssClass: 'bg-[#FFD54F] text-[#000] border border-[#FF7000]',
      icon: 'schedule',
    },
    [EStatus.PARTIALLY_REVIEWED]: {
      label: 'Partially Reviewed',
      cssClass: 'bg-[#FFE0B2] text-[#FF7000] border border-[#FF7000]',
      icon: 'schedule',
    },
    [EStatus.SERVICE_ACCEPTED]: {
      label: 'Service Approved',
      cssClass: 'bg-green-100 text-green-800 border border-green-300',
      icon: 'check_circle',
    },
    [EStatus.SERVICE_REJECTED]: {
      label: 'Service Rejected',
      cssClass: 'bg-red-100 text-red-800 border border-red-300',
      icon: 'cancel',
    },
    [EStatus.USED]: {
      label: 'Used',
      cssClass: 'bg-gray-100 text-gray-800 border border-gray-300',
      icon: 'checklist',
    },
    [EStatus.PENDING_REVIEW]: {
      label: 'Pending Review',
      cssClass: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      icon: 'schedule',
    },

    // Default/fallback
    [EStatus.UNKNOWN]: {
      label: 'Unknown',
      cssClass: 'bg-gray-200 text-gray-800 border border-gray-300',
      icon: 'help_outline',
    },
  };

  private readonly knownStatuses = new Set<string>(Object.values(EStatus));

  transform(value: string | EStatus | null | undefined): StatusBadgeConfig {
    if (!value) return this.statusMap[EStatus.UNKNOWN];

    const normalized = this.normalizeStatus(value);
    if (normalized && this.statusMap[normalized]) {
      return this.statusMap[normalized];
    }

    const labelSource = value?.toString() ?? '';
    return {
      label: this.formatLabel(labelSource),
      cssClass: 'bg-gray-200 text-gray-800 border border-gray-300',
      icon: 'help_outline',
    };
  }

  private normalizeStatus(value: string | EStatus): EStatus | null {
    const normalized = value.toString().toUpperCase().trim();
    return this.knownStatuses.has(normalized) ? (normalized as EStatus) : null;
  }

  private formatLabel(value: string): string {
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
